import { type FC } from "react"

import { type AddonDetails, type AddonType } from "@forento/core/models/payment"
import {
	type AppSumoPlatformPlan,
	type ProductCanyonPlatformPlan,
	type VieDeDinguePlatformPlan,
} from "@forento/core/models/platform"
import Tooltip from "@forento/ui/components/Tooltip"

import Button, { type ButtonVariant } from "~/components/Button"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import trpc, { query } from "~/utilities/trpc"

import Plan, {
	PlanBillingInterval,
	PlanDescription,
	PlanDivider,
	PlanInterval,
	PlanPrice,
	PlanTitle,
	PlanUspContainer,
	PlanUspIcon,
	PlanUspIconContainer,
	PlanUsps,
	PlanUspText,
} from "../Plan"

type Props = {
	type: AddonType
	details: AddonDetails
	platformPlan: AppSumoPlatformPlan | VieDeDinguePlatformPlan | ProductCanyonPlatformPlan
	isMonthlySelected: boolean
	isSubmitting: boolean
	isDisabled: boolean
	setSubmitting(value: boolean): void
	onCancel(): void
}
const Addon: FC<Props> = ({
	type,
	details,
	platformPlan,
	isMonthlySelected,
	isSubmitting,
	isDisabled,
	setSubmitting,
	onCancel,
}) => {
	const user = useUser()
	const platform = usePlatform()!
	const alert = useAlert()

	const billingPortalUrl = query.payment.creator.getBillingPortalUrl.useMutation({
		async onSuccess(url) {
			if (url === null) {
				console.error("Failed to get billing portal URL!")
				await alert.show("Error", "Failed to open payment method management page. Please contact support.")
				return
			}
			window.open(url, "_blank", "noopener,noreferrer")
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to open payment method management page. Please try again later.")
		},
	})

	const callToAction: {
		variant: ButtonVariant
		label: string
		action: "start" | "cancel"
		disabled?: boolean
		tooltip?: string
	} = (() => {
		if (platformPlan.addon) {
			const hasTrial = platform.trialEndDate !== null && platform.trialEndDate > new Date()
			if (platformPlan.addon.type === "trial") {
				return { variant: "primary", label: "Subscribe to addon", action: "start" }
			}
			if (platformPlan.addon.type !== type) {
				if (hasTrial) {
					return {
						variant: "primary",
						label: "Switch to this plan",
						action: "start",
						disabled: true,
						tooltip: "You can't switch to another plan during your free trial.",
					}
				}
				return { variant: "secondary", label: "Switch to this plan", action: "start" }
			}
			if (platformPlan.addon.endDate) {
				return { variant: "primary", label: "Resume plan", action: "start" }
			} else {
				if ((platformPlan.addon.paymentInterval === "month") === isMonthlySelected) {
					return { variant: "secondary-danger", label: "Cancel plan", action: "cancel" }
				} else {
					if (hasTrial) {
						return {
							variant: "primary",
							label: "Switch to this plan",
							action: "start",
							disabled: true,
							tooltip: "You can't switch to another plan during your free trial.",
						}
					}
					return {
						variant: "secondary",
						label: `Switch to ${isMonthlySelected ? "monthly" : "annual"} payment`,
						action: "start",
					}
				}
			}
		}
		return { variant: "primary", label: "Start plan", action: "start" }
	})()

	async function handleSubmit() {
		if (callToAction.action === "cancel") {
			onCancel()
			return
		}
		setSubmitting(true)
		try {
			const { redirectUrl } = await trpc.payment.creator.addAddon.mutate({
				type,
				paymentInterval: isMonthlySelected ? "month" : "year",
				source: "settings",
			})
			if (redirectUrl) {
				window.location.assign(redirectUrl)
			}
			await user.reload()
		} catch (error) {
			console.error(error)
			setSubmitting(false)
		}
		setSubmitting(false)
	}

	const callToActionButton = (
		<Button
			variant={callToAction.variant}
			onClick={handleSubmit}
			isLoading={isSubmitting}
			isDisabled={isDisabled || callToAction.disabled}
			className="w-full"
		>
			{callToAction.label}
		</Button>
	)

	return (
		<Plan>
			<PlanTitle>{details.title}</PlanTitle>
			<PlanPrice>
				${isMonthlySelected ? details.prices.month : details.prices.year / 12}
				<PlanInterval>/month</PlanInterval>
			</PlanPrice>
			<PlanBillingInterval>Billed {isMonthlySelected ? "monthly" : "annually"}</PlanBillingInterval>
			<PlanDescription>{details.description}</PlanDescription>
			<PlanDivider />
			<PlanUsps>
				{details.usps.map((usp, index) => (
					<PlanUspContainer key={index}>
						<PlanUspIconContainer>
							<PlanUspIcon />
						</PlanUspIconContainer>
						<PlanUspText>{usp}</PlanUspText>
					</PlanUspContainer>
				))}
			</PlanUsps>
			<PlanDivider />
			{platformPlan.addon?.type === type && (
				<div className="mb-3">
					<Button
						variant="primary"
						onClick={() => billingPortalUrl.mutate()}
						isLoading={billingPortalUrl.isPending}
						className="w-full"
					>
						Manage payment method
					</Button>
				</div>
			)}
			{callToAction.tooltip ? (
				<Tooltip tooltip={callToAction.tooltip} className="w-full">
					{callToActionButton}
				</Tooltip>
			) : (
				callToActionButton
			)}
		</Plan>
	)
}

export default Addon
